import React from "react";
import * as CHQ from "chq";

import { EventLink, PostLink, ProfileLink, StoryTrendLink } from "app/links";
import { Trans } from "app/utils";
import AnnouncementLink from "app/links/AnnouncementLink";
import SurveyLink from "app/links/SurveyLink";
import UserClaimedLink from "app/links/UserClaimedLink";

import NotificationBody from "./NotificationBody";

type UserActivityCheeredNotificationProps = {
  notification: CHQ.NotificationUserActivityCheered;
  tabIndex?: number;
};

type UserActivityCheerableProps = {
  actor: null | CHQ.User;
  cheerable: CHQ.UserActivity;
  tabIndex?: number;
};

type _UserClaimedCheerableProps = {
  actor: null | CHQ.User;
};

const PostCheeredNotification: React.FC<UserActivityCheerableProps> = ({
  actor, cheerable, tabIndex
}) => (
  <NotificationBody actor={actor}>
    <Trans>
      <ProfileLink user={actor} tabIndex={tabIndex || 0} />
      {" "}
      cheered your post:
      {" "}
      <PostLink post={cheerable.entity} tabIndex={tabIndex || 0}>
        {cheerable.entity.title}
      </PostLink>
    </Trans>
  </NotificationBody>
);

const StoryCheeredNotification: React.FC<UserActivityCheerableProps> = ({
  actor, cheerable, tabIndex
}) => (
  <NotificationBody actor={actor}>
    <Trans>
      <ProfileLink user={actor} tabIndex={tabIndex || 0} />
      {" "}
      cheered your story trend:
      {" "}
      <StoryTrendLink story={cheerable.entity} tabIndex={tabIndex || 0}>
        {cheerable.entity.question.question}
      </StoryTrendLink>
    </Trans>
  </NotificationBody>
);

const EventCheeredNotification: React.FC<UserActivityCheerableProps> = ({
  actor, cheerable, tabIndex
}) => (
  <NotificationBody actor={actor}>
    <Trans>
      <ProfileLink user={actor} tabIndex={tabIndex || 0} />
      {" "}
      cheered your event:
      {" "}
      <EventLink event={cheerable.entity} tabIndex={tabIndex || 0}>
        {cheerable.entity.name}
      </EventLink>
    </Trans>
  </NotificationBody>
);

const SurveyCheeredNotification: React.FC<UserActivityCheerableProps> = ({
  actor, cheerable, tabIndex
}) => (
  <NotificationBody actor={actor}>
    <Trans>
      <ProfileLink user={actor} tabIndex={tabIndex || 0} />
      {" "}
      cheered your
      {" "}
      <SurveyLink survey={cheerable.entity} tabIndex={tabIndex || 0}>survey</SurveyLink>
      {": "}
      {cheerable.entity.title}
    </Trans>
  </NotificationBody>
);

const ApprovedForSocialCheeredNotification: React.FC<UserActivityCheerableProps> = ({
  actor, cheerable, tabIndex
}) => (
  <NotificationBody actor={actor}>
    <Trans>
      <ProfileLink user={actor} tabIndex={tabIndex || 0} />
      {" "}
      cheered your
      {" "}
      Approval for Social
      {": "}
      {cheerable.entity.entity.question.question}
    </Trans>
  </NotificationBody>
);

const SocialShareCheeredNotification: React.FC<UserActivityCheerableProps> = ({
  actor, cheerable, tabIndex
}) => (
  <NotificationBody actor={actor}>
    <Trans>
      <ProfileLink user={actor} tabIndex={tabIndex || 0} />
      {" "}
      cheered your
      {" "}
      Social share
      {": "}
      {cheerable.entity.entity.question.question}
    </Trans>
  </NotificationBody>
);

const AnnouncementCheeredNotification: React.FC<UserActivityCheerableProps> = ({
  actor, cheerable, tabIndex
}) => (
  <NotificationBody actor={actor}>
    <Trans>
      <ProfileLink user={actor} tabIndex={tabIndex || 0} />
      {" "}
      cheered your
      {" "}
      <AnnouncementLink announcement={cheerable.entity} tabIndex={tabIndex || 0}>
        featured content
      </AnnouncementLink>
    </Trans>
  </NotificationBody>
);

const UserClaimedCheeredNotification: React.FC<UserActivityCheerableProps> = ({
  actor, cheerable, tabIndex
}) => (
  <NotificationBody actor={actor}>
    <Trans>
      <ProfileLink user={actor} tabIndex={tabIndex || 0} />
      {" "}
      cheered your
      {" "}
      <UserClaimedLink user={cheerable.entity} tabIndex={tabIndex || 0}>
        entry to the platform
      </UserClaimedLink>
    </Trans>
  </NotificationBody>
);

const UserActivityCheeredNotification: React.FC<UserActivityCheeredNotificationProps> = ({
  notification: { actor, entity: { cheerable } }, tabIndex
}) => {
  switch (cheerable.activityType) {
    case "announcement_created":
      return (
        <AnnouncementCheeredNotification actor={actor} cheerable={cheerable} tabIndex={tabIndex} />
      );
    case "approved_for_social":
      return (
        <ApprovedForSocialCheeredNotification
          actor={actor}
          cheerable={cheerable}
          tabIndex={tabIndex}
        />
      );
    case "event_created":
      return (
        <EventCheeredNotification actor={actor} cheerable={cheerable} tabIndex={tabIndex} />
      );
    case "post_created":
      return (
        <PostCheeredNotification actor={actor} cheerable={cheerable} tabIndex={tabIndex} />
      );
    case "story_created":
      return (
        <StoryCheeredNotification actor={actor} cheerable={cheerable} tabIndex={tabIndex} />
      );
    case "user_claimed":
      return (
        <UserClaimedCheeredNotification actor={actor} cheerable={cheerable} tabIndex={tabIndex} />
      );
    case "widget_survey_activated":
      return (
        <SurveyCheeredNotification actor={actor} cheerable={cheerable} tabIndex={tabIndex} />
      );
    case "social_share":
      return (
        <SocialShareCheeredNotification actor={actor} cheerable={cheerable} tabIndex={tabIndex} />
      );
    default:
      return null;
  }
};

export default UserActivityCheeredNotification;
